import { useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from 'react-oidc-context';
import { selectOptionalUser } from 'features/auth/store/authSelectors';
import { isPageSessionStale } from 'features/auth/utils/appRenderRule';
import { getAppLocale } from 'features/i18n/utils/getAppLocale';
import { useStartFlow } from 'features/loading';
import { useAppSelector } from 'features/store/hooks';
import type { IdentityTokenUserSettings } from '@hub/common';

const signInLocale = (accessToken: string): string | undefined => {
  if (isPageSessionStale()) return undefined;
  try {
    return (
      jwtDecode<IdentityTokenUserSettings>(accessToken).locale ?? undefined
    );
  } catch {
    return undefined;
  }
};

/** Gives the start flow the language of the Identity token the sign-in uses
 *  as soon as that token arrives, and the user's saved language once sign-in
 *  has read it. Renders inside the oidc `AuthProvider`. */
export const StartFlowLocaleReporter = (): null => {
  const { user, isAuthenticated } = useAuth();
  const { active, setLocale, setSavedLocale } = useStartFlow();
  const accessToken = isAuthenticated ? user?.access_token : undefined;
  const savedLanguage = useAppSelector(
    state => selectOptionalUser(state)?.language
  );

  useEffect(() => {
    if (!active || !accessToken) return;
    const locale = signInLocale(accessToken);
    if (locale) setLocale(getAppLocale(locale));
  }, [active, accessToken, setLocale]);

  useEffect(() => {
    if (active && savedLanguage) setSavedLocale(getAppLocale(savedLanguage));
  }, [active, savedLanguage, setSavedLocale]);

  return null;
};
